import React, { FunctionComponent, useContext } from "react";
import { PageContext, PageContextProps } from "/src/contexts/Page";
import { Header } from "/src/ui/shared/header";
import { Footer } from "/src/ui/shared/footer";
import { ThemeProvider, StyledEngineProvider, Button, GlobalStyles } from "@mui/material";
import {
  makeStyles,
  ThemeProvider as ThemeProviderForStyles,
} from "@mui/styles";
import { useTranslation } from "react-i18next";
import { ChallengeUser } from "/src/domain/challenges/ChallengeUser";
import { ChallengeRegistrationData } from "/src/domain/challenges/ChallengeRegistrationData";
import { PageConfig } from "/src/domain/shared/Page";
import { theme } from "/src/assets/theme";
import { UpcomingRacesContainer } from "/src/ui/shared/upcomingRaces";
import { TeamBuildingBanner } from "/src/ui/shared/TeamBuildingBanner";
import styledTheme from "/src/ui/shared/styledTheme";
import { AuthorizedTeamDetails } from "/src/ui/challengesRegistration/teamRegistration/AuthorizedTeamDetails";
import { PredictionsView } from "/src/ui/challengesRegistration/prediction/Predictions";
import { RegistrationForm } from "/src/ui/challengesRegistration/registrationForm/RegistrationForm";
import { ChallengeDescriptionView } from "/src/ui/shared/ChallengeDescriptionView";
import { ChallengeRegistrationProvider } from "/src/contexts/ChallengeRegistrationContext";
import { ChallengeFormula } from "../../domain/challenges/ChallengeProtocolData";
import { links } from "/src/domain/shared/links";

const ChallengeRegistrationApp: FunctionComponent = () => {
  const { pageData, currentUser, pageConfig } = useContext(
    PageContext
  ) as PageContextProps;

  if (pageData === null) return null;
  const challengeData = pageData as ChallengeRegistrationData;
  const challengeUser = currentUser as ChallengeUser;

  return (
    <StyledEngineProvider injectFirst>
      <GlobalStyles
        styles={{
          'body': {
            backgroundColor: theme.WHITE_COLOR,
          },
        }}
      />
      <ThemeProvider theme={styledTheme}>
        <ThemeProviderForStyles theme={styledTheme}>
          <ChallengeRegistrationProvider>
            <App
              challengeData={challengeData}
              currentUser={challengeUser}
              pageConfig={pageConfig}
            />
          </ChallengeRegistrationProvider>
        </ThemeProviderForStyles>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

interface AppProps {
  challengeData: ChallengeRegistrationData;
  currentUser?: ChallengeUser;
  pageConfig: PageConfig | null;
}

const App: FunctionComponent<AppProps> = ({
  challengeData,
  currentUser,
  pageConfig,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <>
      {pageConfig?.header && <Header currentUser={currentUser} />}
      <div className={classes.contentContainer}>
        <h2 className={classes.header}>{t("registerRace")}</h2>

        <ChallengeDescriptionView
          challenge={challengeData.challenge}
          className={classes.description}
        />

        {!challengeData.isActive && (
          <h3 className={classes.registrationClosed}>
            {t("registrationClosed")}
          </h3>
        )}
        {Boolean(currentUser) && challengeData.isActive && (
          <RegistrationForm
            challengeData={challengeData}
            currentUser={currentUser!!}
            extraFields={pageConfig?.extraFields}
          />
        )}

        <PredictionsView
          withPredictions={Boolean(pageConfig?.predictions)}
          currentUser={currentUser}
          isRegActive={challengeData.isActive}
        />

        {Boolean(currentUser?.challengeRegistration?.isRegistered) &&
          [ChallengeFormula.TEAM_STEPPER, ChallengeFormula.STEPPER].includes(
            challengeData.challenge.formulaType
          ) && (
            <Button
              className={classes.startlistBtn}
              href={`${links.origin}/login-google`}
              color="secondary"
              variant="contained"
            >
              {t("connectGoogle")}
            </Button>
          )}

        <AuthorizedTeamDetails
          isRegActive={challengeData.isActive}
          teamRegistration={challengeData.teamRegistration}
          currentUser={currentUser}
        />

        {Boolean(currentUser?.challengeRegistration?.isRegistered) &&
          challengeData.challenge.formulaType === ChallengeFormula.IPPODROM && (
            <Button
              className={classes.startlistBtn}
              href={`/on-start?id=${challengeData.challengeKey}`}
              color="secondary"
              variant="contained"
            >
              {t("startlistHeader")}
            </Button>
          )}
        {!Boolean(currentUser) && (
          <div className={classes.loginContainer}>
            <Button
              className={classes.loginBtn}
              variant={"contained"}
              color={"secondary"}
              href={pageConfig!!.authUrl}
            >
              {t("login")}
            </Button>
          </div>
        )}
        {!Boolean(pageConfig?.isChallengesLinkDisabled) && (
          <a className={classes.link} href={pageConfig?.challengesLink}>
            {t("goToChallenges")}
          </a>
        )}
        {Boolean(pageConfig?.shopBanner) && <TeamBuildingBanner />}
        <UpcomingRacesContainer startReg={challengeData.startreg} />
      </div>
      {pageConfig?.footer && <Footer />}
    </>
  );
};

const useStyles = makeStyles((muiTheme) => ({
  contentContainer: {
    [muiTheme.breakpoints.up("sm")]: {
      maxWidth: "1100px",
      minHeight: "calc(100vh - 18rem)",
      margin: "0 auto",
    },
    minHeight: "calc(100vh - 21rem)",
  },
  description: {
    marginTop: "1.5rem",
  },
  header: {
    color: theme.WHITE_COLOR,
    marginTop: "1.5rem",
    padding: "1rem 5rem 1rem 1.5rem",
    fontSize: "1.5rem",
    fontWeight: 500,
    background: `url(${require("../../assets/icons/primary_rect.svg")})`,
    display: "inline-block",
    backgroundPositionX: "right",
    backgroundSize: "cover",
  },
  registrationClosed: {
    width: "100%",
    textAlign: "center",
    fontSize: "1.25rem",
    color: "#000",
    margin: "1.5rem 0",
  },
  link: {
    color: "#000",
    marginTop: "0.25rem",
    textAlign: "center",
    width: "100%",
    display: "block",
  },
  loginContainer: {
    margin: "1rem 0",
    display: "flex",
    justifyContent: "center",
  },
  loginBtn: {
    minWidth: "25rem",
    height: "48px",
  },
  startlistBtn: {
    display: "block",
    textAlign: "center",
    width: "20rem",
    margin: "1rem auto",
  },
}));

export default ChallengeRegistrationApp;
